import { apiFetch, ApiError } from "./apiClient";
import { mockApi } from "./mockData";

// ─── Access level helpers ────────────────────────────────────────────────────

const ACCESS_LEVEL_HIERARCHY = { basic: 1, standard: 2, premium: 3, admin: 4 };

/**
 * Maps backend product_type strings to the minimum access level required.
 * Keep in sync with INDICATOR_REQUIRED_LEVELS in SideMenu.jsx.
 */
const PRODUCT_ACCESS_REQUIREMENTS = {
  NDVI: "basic",
  LNC:  "basic",
  CH:   "basic",
  EVI:  "basic",
  SAVI: "basic",
  LWC:  "standard",
  NDWI: "standard",
  MNDWI:"standard",
  LST:  "standard",
  TA:   "standard",
  CWSI: "premium",
  CWSI_THERMAL: "premium",
  CWSI_OPTIC:   "premium",
  ETa:  "premium",
  IWS:  "premium",
  SM:   "premium",
  YIELD:"premium",
  OPTRAM: "premium",
  METRIC: "premium",
  GPP:  "premium",
  FAPAR:"premium",
  LAI:  "premium",
};

const normalizeLevel = (level) => {
  const n = String(level || "basic").toLowerCase();
  return ACCESS_LEVEL_HIERARCHY[n] !== undefined ? n : "basic";
};

export const userHasProductAccess = (productType, accessLevel) => {
  const required = PRODUCT_ACCESS_REQUIREMENTS[productType] || "basic";
  return (
    (ACCESS_LEVEL_HIERARCHY[normalizeLevel(accessLevel)] ?? 1) >=
    (ACCESS_LEVEL_HIERARCHY[required] ?? 1)
  );
};

// ─── User access level ───────────────────────────────────────────────────────

/**
 * GET /api/access-level/
 * Cached for the lifetime of the page — never called more than once per session.
 */
let _cachedAccessLevel = null;

export async function fetchUserAccessLevel() {
  if (_cachedAccessLevel !== null) return { access_level: _cachedAccessLevel };

  const data = await apiFetch("/api/access-level/", {
    method: "GET",
    mockHandler: () => ({ access_level: "basic" }),
  });

  _cachedAccessLevel = data?.access_level || "basic";
  return { access_level: _cachedAccessLevel };
}

/** Call this on logout so next login re-fetches the correct level. */
export function clearAccessLevelCache() {
  _cachedAccessLevel = null;
}

// ─── Product list ─────────────────────────────────────────────────────────────

/**
 * GET /api/products/list/?product_type={type}
 *
 * - Accepts an already-resolved accessLevel string to avoid a second request.
 *   If omitted, fetches (and caches) the level automatically.
 * - Throws ApiError(403) immediately (before hitting the products endpoint)
 *   when the user's level is insufficient.
 * - Returns [] when the backend returns an empty list rather than throwing,
 *   so callers can distinguish "no products" from errors.
 */
export async function listProducts(productType, accessLevel = null) {
  // Resolve access level — uses cache after the first call
  const level =
    accessLevel ||
    (await fetchUserAccessLevel())?.access_level ||
    "basic";

  // Guard before network call
  if (!userHasProductAccess(productType, level)) {
    throw new ApiError(
      `دسترسی شما به محصول ${productType} محدود شده است.`,
      {
        status: 403,
        body: { required: PRODUCT_ACCESS_REQUIREMENTS[productType] || "basic" },
      },
    );
  }

  const productList = await apiFetch(
    `/api/products/list/?product_type=${encodeURIComponent(productType)}`,
    {
      method: "GET",
      mockHandler: () => mockApi.getProductList(productType),
    },
  );

  // Return empty array gracefully instead of throwing on empty list
  return Array.isArray(productList) ? productList : [];
}

// ─── Product metadata ─────────────────────────────────────────────────────────

/**
 * GET /api/products/{id}/metadata/
 */
export async function getProductMetadata(productId, productType) {
  return apiFetch(`/api/products/${productId}/metadata/`, {
    method: "GET",
    mockHandler: () => mockApi.getProductMetadata(productId, productType),
  });
}

/**
 * Resolves the active (latest) product and loads its metadata.
 * Returns null if no products exist rather than throwing, so the caller
 * can show a "no data" state without flooding the server.
 */
export async function resolveActiveProduct(productType, accessLevel = null) {
  const list = await listProducts(productType, accessLevel);

  if (!list.length) return null; // no products — caller handles UI state

  const sorted = [...list].sort((a, b) =>
    String(b.acquired_at || b.date || "").localeCompare(
      String(a.acquired_at || a.date || ""),
    ),
  );
  const product = sorted[0];

  let metadata = null;
  try {
    metadata = await getProductMetadata(product.id, productType);
  } catch {
    metadata = mockApi.getProductMetadata(product.id, productType);
  }

  return {
    product,
    metadata,
    minValue: metadata?.min_value ?? product.min_value,
    maxValue: metadata?.max_value ?? product.max_value,
    legend: metadata?.legend,
    visualization: metadata?.visualization,
  };
}

// ─── Tile URL ─────────────────────────────────────────────────────────────────

/**
 * Tile URL template for Leaflet.
 * Auth token appended as a query param so tile requests are authenticated.
 */
export function getProductTileUrl(productId, { min, max } = {}) {
  const token = localStorage.getItem("authToken");
  const params = new URLSearchParams();
  if (min != null) params.set("min", String(min));
  if (max != null) params.set("max", String(max));
  if (token) params.set("token", token);
  const qs = params.toString();
  return `/api/products/${productId}/tiles/{z}/{x}/{y}.png${qs ? `?${qs}` : ""}`;
}
